/**
 * useMessagingSocket
 * ------------------
 * ONE app-wide WebSocket for user-to-user messaging. Mounted once in
 * MainLayout (same pattern as useProfileCompletionGate) so it lives for the
 * whole authenticated session.
 *
 * - Connects lazily once authenticated; URL is derived from API_PREFIX
 *   (`wss://<api-host>/api/v1/messages/ws?token=…` in prod, where the SPA and
 *   API are on different hosts; same-origin `/v1` in dev so the Vite proxy
 *   handles the upgrade).
 * - Exponential-backoff reconnect (1s → 30s cap), heartbeat ping every 25s.
 * - Fans incoming server events out as window CustomEvents (DM_EVENTS.*) AND
 *   keeps the react-query caches live: invalidates ['dm-threads'] /
 *   ['dm-unread'] and appends to ['dm-messages', thread_id] when that thread's
 *   cache exists.
 * - After a reconnect, invalidates all DM queries to resync missed events.
 *
 * `sendTyping(threadId)` is exported as a plain function so any component can
 * emit typing signals without re-plumbing the socket.
 */
import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/shared/AuthContext';
import { tokenStorage } from '@/api/tokenManager';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import {
  DM_EVENTS,
  DM_QUERY_KEYS,
  type DmMessage,
  type DmMessageListResponse,
} from '@/services/messaging.service';
import { parseMultilang } from '@/utils/multilang';

const PING_INTERVAL_MS = 25_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/** Module-level so sendTyping works from anywhere without prop drilling. */
let activeSocket: WebSocket | null = null;

export function sendTyping(threadId: number): void {
  try {
    if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
      activeSocket.send(JSON.stringify({ type: 'typing', thread_id: threadId }));
    }
  } catch {
    /* non-fatal */
  }
}

/** Socket events skip the API client, which shows every stored-in-several-languages text (people's names,
 *  {en}…{en}{ar}…{ar}) in the page's language: do the same here. */
function resolveLanguages(value: unknown): unknown {
  if (typeof value === 'string') {
    if (!value.includes('{')) return value;
    let lang = 'en';
    try { lang = (localStorage.getItem('ilearn_active_locale') || 'en').toLowerCase(); } catch { /* default */ }
    return parseMultilang(value, lang);
  }
  if (Array.isArray(value)) return value.map(resolveLanguages);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, resolveLanguages(v)]));
  }
  return value;
}

/** Append a live message to an already-populated thread cache (dedup by id). */
function appendMessageToCache(
  queryClient: QueryClient,
  threadId: number,
  message: DmMessage
): void {
  queryClient.setQueryData<DmMessageListResponse | undefined>(
    DM_QUERY_KEYS.messages(threadId),
    (old) => {
      if (!old) return old; // thread never opened — nothing to append to
      if (old.items.some((m) => m.id === message.id)) return old;
      return { ...old, items: [...old.items, message] };
    }
  );
}

interface ServerEvent {
  type?: string;
  thread_id?: number;
  message?: DmMessage;
  user_id?: number;
  name?: string;
  last_read_at?: string;
}

export function useMessagingSocket(): void {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    let disposed = false;
    let ws: WebSocket | null = null;
    let backoff = BACKOFF_MIN_MS;
    let reconnectTimer: number | null = null;
    let pingTimer: number | null = null;
    let hadConnection = false;

    const clearPing = () => {
      if (pingTimer != null) {
        window.clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, backoff);
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
    };

    const handleEvent = (data: ServerEvent) => {
      switch (data?.type) {
        case 'message.new': {
          if (data.thread_id == null || !data.message) break;
          appendMessageToCache(queryClient, data.thread_id, data.message);
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.threads });
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.unread });
          window.dispatchEvent(
            new CustomEvent(DM_EVENTS.NEW_MESSAGE, {
              detail: { thread_id: data.thread_id, message: data.message },
            })
          );
          break;
        }
        case 'thread.read': {
          if (data.thread_id == null) break;
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.threads });
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.unread });
          window.dispatchEvent(
            new CustomEvent(DM_EVENTS.THREAD_READ, {
              detail: {
                thread_id: data.thread_id,
                user_id: data.user_id,
                last_read_at: data.last_read_at,
              },
            })
          );
          break;
        }
        case 'typing': {
          if (data.thread_id == null) break;
          window.dispatchEvent(
            new CustomEvent(DM_EVENTS.TYPING, {
              detail: {
                thread_id: data.thread_id,
                user_id: data.user_id,
                name: data.name ?? '',
              },
            })
          );
          break;
        }
        case 'pong':
        default:
          break;
      }
    };

    const connect = () => {
      if (disposed) return;
      const token = tokenStorage.getAccessToken();
      if (!token) {
        scheduleReconnect();
        return;
      }

      // Prefer the configured API host (in prod the SPA and the API live on
      // different hosts); fall back to same-origin so the Vite dev proxy still
      // handles the upgrade.
      const apiBase = API_PREFIX || '/v1';
      const wsBase = /^https?:\/\//.test(apiBase)
        ? apiBase.replace(/^http/, 'ws')
        : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${apiBase}`;
      let next: WebSocket;
      try {
        next = new WebSocket(
          `${wsBase}/messages/ws?token=${encodeURIComponent(token)}`
        );
      } catch {
        scheduleReconnect();
        return;
      }
      ws = next;
      activeSocket = next;

      next.onopen = () => {
        if (disposed) return;
        backoff = BACKOFF_MIN_MS;
        if (hadConnection) {
          // We were offline — resync everything DM-related.
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.threads });
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.unread });
          queryClient.invalidateQueries({ queryKey: DM_QUERY_KEYS.messagesRoot });
        }
        hadConnection = true;
        clearPing();
        pingTimer = window.setInterval(() => {
          if (next.readyState === WebSocket.OPEN) {
            try {
              next.send(JSON.stringify({ type: 'ping' }));
            } catch {
              /* non-fatal */
            }
          }
        }, PING_INTERVAL_MS);
      };

      next.onmessage = (evt) => {
        if (disposed) return;
        try {
          handleEvent(resolveLanguages(JSON.parse(evt.data)) as ServerEvent);
        } catch {
          /* malformed frame — ignore */
        }
      };

      next.onclose = () => {
        clearPing();
        if (activeSocket === next) activeSocket = null;
        scheduleReconnect();
      };

      next.onerror = () => {
        // onclose fires afterwards and handles the reconnect.
        try {
          next.close();
        } catch {
          /* already closed */
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      clearPing();
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null; // don't reconnect on intentional teardown
        try {
          ws.close();
        } catch {
          /* already closed */
        }
        if (activeSocket === ws) activeSocket = null;
      }
    };
  }, [isAuthenticated, queryClient]);
}
